import * as React from 'react';
import { Button, Form, FormControl, FormGroup, FormLabel, Modal, Spinner, Stack, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { getConfigOrThrow } from 'gossamer-front-end';

export const ChameleonLogin = (): React.JSX.Element => {
  const [isLoading, setLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string>('');
  const [success, setSuccess] = React.useState<string>('');

  const staffEmailRef = React.useRef<HTMLInputElement>(null);
  const staffPasswordRef = React.useRef<HTMLInputElement>(null);
  const targetUserEmailRef = React.useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  const handleFormSubmit = (event: React.FormEvent) => {
    chameleonLogin();
    event.preventDefault();
  };

  const chameleonLogin = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');

      const staffEmail = staffEmailRef.current?.value;
      const staffPassword = staffPasswordRef.current?.value;
      const targetUserEmail = targetUserEmailRef.current?.value;

      if (!staffEmail || !staffPassword || !targetUserEmail) {
        setError('All fields are required');
        setLoading(false);
        return;
      }

      const response = await fetch(`${getConfigOrThrow('gossamer-be-origin')}/gossamer/main/chameleon-login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          staffEmail,
          staffPassword,
          targetUserEmail,
        }),
      });

      const data = await response.json();

      if (response.ok && data.status === 'OK') {
        setSuccess(data.message);
        setLoading(false);

        // Reload the page to pick up the new session
        setTimeout(() => {
          window.location.href = '/admin';
        }, 1000);
      } else {
        setError(data.message || 'Failed to perform chameleon login');
        setLoading(false);
      }
    } catch (err) {
      console.error('Chameleon login error:', err);
      setError('An unexpected error occurred');
      setLoading(false);
    }
  };

  return (
    <Modal show={true} backdrop="static" centered={true} size="lg">
        <Modal.Header>
          <Modal.Title>Chameleon Login (Staff Only)</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="warning">
            <strong>Warning:</strong> This feature allows staff members to impersonate other users. Use responsibly and
            only for legitimate support purposes.
          </Alert>

          {error && <Alert variant="danger">{error}</Alert>}
          {success && <Alert variant="success">{success}</Alert>}

          <Form onSubmit={handleFormSubmit}>
            <Stack gap={3}>
              <h6>Your Staff Credentials</h6>

              <FormGroup controlId="staffEmail">
                <FormLabel>Staff Email</FormLabel>
                <FormControl
                  type="email"
                  ref={staffEmailRef}
                  autoComplete="username"
                  placeholder="your.email@careerbook.co.uk"
                  disabled={isLoading}
                />
              </FormGroup>

              <FormGroup controlId="staffPassword">
                <FormLabel>Staff Password</FormLabel>
                <FormControl
                  type="password"
                  ref={staffPasswordRef}
                  autoComplete="current-password"
                  disabled={isLoading}
                />
              </FormGroup>

              <hr />

              <h6>Target User</h6>

              <FormGroup controlId="targetUserEmail">
                <FormLabel>User Email to Impersonate</FormLabel>
                <FormControl
                  type="email"
                  ref={targetUserEmailRef}
                  placeholder="user.email@example.com"
                  disabled={isLoading}
                />
              </FormGroup>

              <Stack
                direction="horizontal"
                gap={2}
                style={{
                  justifyContent: 'flex-end',
                }}
              >
                <Button variant="secondary" onClick={() => navigate('/admin')} disabled={isLoading}>
                  Cancel
                </Button>
                <Button variant="primary" type="submit" disabled={isLoading}>
                  {isLoading && (
                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                  )}
                  {isLoading ? 'Logging in...' : 'Login as User'}
                </Button>
              </Stack>
            </Stack>
          </Form>
        </Modal.Body>
    </Modal>
  );
};
